import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, ShieldAlert, LogIn, MessageCircle } from "lucide-react";

const DURATE = [
  { m: 60, l: "1 ora" },
  { m: 180, l: "3 ore" },
  { m: 480, l: "8 ore" },
  { m: 1440, l: "24 ore" },
];

const quando = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const etichetta = (r) =>
  r.status === "pending" ? "in attesa della risposta del titolare"
    : r.status === "approved" ? `autorizzato fino al ${quando(r.access_until)}`
    : r.status === "emergency" ? `emergenza, valido fino al ${quando(r.access_until)}`
    : r.status === "denied" ? "rifiutato dal titolare"
    : r.status === "expired" ? "richiesta decaduta senza risposta" : "accesso chiuso";

/** Entrare nel gestionale di un locale per controllare la configurazione: sempre in sola lettura. */
export const AccessoLocale = ({ client, tenant }) => {
  const [d, setD] = useState(null);
  const [motivo, setMotivo] = useState("controllo del menu e della configurazione");
  const [minuti, setMinuti] = useState(60);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get(`/admin/access?tenant=${encodeURIComponent(tenant.slug)}`);
      setD(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registro accessi non disponibile");
    }
  }, [client, tenant.slug]);

  useEffect(() => {
    load();
  }, [load]);

  const azione = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok);
      await load();
      return r?.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const entra = (token, slug) => {
    localStorage.setItem("rh_tenant_token", token);
    const url = `/?assistenza=1&locale=${slug}`;
    // Su telefono la finestra nuova arriva dopo la risposta del server: Safari la blocca come
    // pop-up e sembra che non accada niente. Se il blocco scatta, si apre nella stessa pagina.
    const w = window.open(url, "_blank");
    if (!w || w.closed) {
      toast.success("Apro il gestionale in sola lettura…");
      window.location.href = url;
    }
  };

  if (!d) return null;
  const aperta = d.gate?.open;
  const attesa = (d.pending || [])[0];
  const pronta = (d.storico || []).find((r) => ["approved", "emergency"].includes(r.status));
  const testoWa = encodeURIComponent(
    `Ciao, sono l'assistenza Reprehendo. Per ${motivo} avrei bisogno di entrare nel tuo gestionale in ` +
    `sola lettura (non modifico nulla). Apri il gestionale, vai in Impostazioni → Accessi del fornitore ` +
    `e autorizza la richiesta: si chiude da sola.`);

  return (
    <div className="space-y-3 rounded-md bg-slate-900/60 p-3" data-testid="accesso-locale">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
        <KeyRound className="h-3.5 w-3.5" /> Entrare nel gestionale (sola lettura)
      </p>

      <p className="text-xs text-slate-400">
        {aperta
          ? d.gate.always
            ? "Il titolare ha lasciato l'accesso aperto: puoi entrare subito."
            : `Il titolare ha aperto l'accesso fino al ${quando(d.gate.until)}.`
          : "Il titolare deve autorizzare: gli arriva un'email e l'avviso dentro il gestionale."}
      </p>

      <Input
        className="h-9"
        placeholder="Motivo (lo legge il titolare)"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        data-testid="accesso-motivo-input"
      />

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-slate-400">Per</span>
        {DURATE.map((o) => (
          <Button key={o.m} size="sm" variant={minuti === o.m ? "default" : "outline"}
                  data-testid={`accesso-durata-${o.m}`} onClick={() => setMinuti(o.m)}>
            {o.l}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {aperta ? (
          <Button size="sm" disabled={busy} data-testid="accesso-entra-aperto"
                  onClick={async () => {
                    const r = await azione(() => client.post("/admin/access/open",
                      { tenant: tenant.slug, reason: motivo, minutes: minuti }), "Sessione di assistenza aperta");
                    if (r?.token) entra(r.token, r.slug);
                  }}>
            <LogIn className="mr-1 h-3.5 w-3.5" /> Entra ora
          </Button>
        ) : (
          <Button size="sm" disabled={busy} data-testid="accesso-chiedi"
                  onClick={() => azione(() => client.post("/admin/access/request",
                    { tenant: tenant.slug, reason: motivo, minutes: minuti }), "Richiesta inviata al titolare")}>
            <KeyRound className="mr-1 h-3.5 w-3.5" /> Chiedi di entrare
          </Button>
        )}

        {pronta && (
          <Button size="sm" variant="outline" disabled={busy} data-testid="accesso-entra"
                  onClick={async () => {
                    const r = await azione(() => client.post(`/admin/access/${pronta.id}/enter`,
                      { tenant: tenant.slug }), "Sessione di assistenza aperta");
                    if (r?.token) entra(r.token, r.slug);
                  }}>
            <LogIn className="mr-1 h-3.5 w-3.5" /> Entra ora (autorizzato)
          </Button>
        )}

        {tenant.phone && (
          <a className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-700 px-2.5 text-xs font-semibold text-emerald-300"
             href={`https://wa.me/${tenant.phone.replace(/[^0-9]/g, "")}?text=${testoWa}`}
             target="_blank" rel="noreferrer" data-testid="accesso-whatsapp">
            <MessageCircle className="h-3.5 w-3.5" /> Chiedi su WhatsApp
          </a>
        )}
      </div>

      <div className="border-t border-slate-800 pt-2">
        <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300" disabled={busy}
                data-testid="accesso-emergenza"
                onClick={() => {
                  if (!window.confirm("Entrare senza attendere? Il titolare riceve un'email e la riga resta in rosso nel suo registro.")) return;
                  azione(() => client.post("/admin/access/emergency",
                    { tenant: tenant.slug, reason: motivo, minutes: minuti }), "Accesso d'emergenza registrato");
                }}>
          <ShieldAlert className="mr-1 h-3.5 w-3.5" /> Entra subito (emergenza)
        </Button>
      </div>

      {(attesa || pronta) && (
        <p className="text-[11px] text-slate-400" data-testid="accesso-stato">
          Ultima richiesta: {etichetta(attesa || pronta)}
        </p>
      )}
    </div>
  );
};
